import {
  Box,
  Button,
  Flex,
  HStack,
  Link,
  Text,
  Icon,
  Grid,
  GridItem,
} from '@everlywell/ui-kit';
import { ArrowRight, ArrowSquareOut } from '@phosphor-icons/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { useCart } from 'components/Cart';
import { DisclaimerDataProps } from 'components/GeneralDisclaimer';
import { ScreenerDataProps } from 'components/PrePurchaseScreener';
import Price from 'components/Price';
import { MembershipOptionType } from 'components/PurchaseOptionsDrawerProvider/components/PurchaseOptionsDrawer';
import StripePayment, {
  StripePaymentProps,
} from 'components/Stripe/StripePayment';
import StripeMerchandisingWidget from 'components/StripeMerchandisingWidget';
import { useToastContext } from 'components/ToastContext/ToastContext';
import {
  PrePurchaseModalActionsTypes,
  usePrePurchaseModalContext,
} from 'contexts/PrePurchaseModal';
import { Link as GatsbyLink } from 'gatsby';
import { navigate } from 'gatsby';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import Cookies from 'js-cookie';
import React, { useCallback, useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { processExpressPayOrder } from 'utils/api/cartApis';
import { ANALYTICS } from 'utils/constants/analytics';
import { CHECKOUT_QUEST_BASE_PATH } from 'utils/constants/checkout';
import { CookieNames } from 'utils/constants/cookies';
import { ELIMINATION_DIET_COACHING_SLUG } from 'utils/constants/products';
import { VITAMINS_AND_SUPPLEMENTS } from 'utils/constants/vitaminsAndSupplements';
import { logError } from 'utils/helpers';
import { discountedPrice, Promo } from 'utils/helpers/activePromoHelpers';
import { stripePublicKey } from 'utils/helpers/paymentHelpers';

import { useSelectedOptionsContext } from '../../SelectedOption.context';
import IncentivesList from '../IncentivesList';
import MembershipOptions from './components/MembershipOptions';
import SimplePurchaseOption from './components/SimplePurchaseOption';

export type PurchaseOptionsProps = {
  product: Queries.pdpSpreeProductFragment;
  isProductOutOfStock?: boolean;
  hasPrePurchaseStep?: boolean;
  screener?: ScreenerDataProps;
  disclaimer?: DisclaimerDataProps;
  isQuestProduct?: boolean;
  contentfulFlags?: Queries.pdpContentfulMetadata001Fragment['flags'];
  showPromo?: boolean;
  activePromo: Promo | null;
  hidePaymentOptions?: boolean;
};

/**
 * PurchaseOptions component displays the payment options for a product
 */
const PurchaseOptions = (props: PurchaseOptionsProps) => {
  const {
    product,
    isProductOutOfStock,
    hasPrePurchaseStep,
    screener,
    disclaimer,
    isQuestProduct,
    contentfulFlags,
    showPromo,
    activePromo,
    hidePaymentOptions,
  } = props;

  const { sku, variants, master_variant_id: productVariantId, name } = product;

  const { enableStripeUpgrade } = useFlags();

  /**
   * If the product is EDC or a vitamin, do not show incentives
   */

  const isEliminationDiet = product?.slug === ELIMINATION_DIET_COACHING_SLUG;

  const productVariant = variants?.find(
    (variant) => variant?.id === `${productVariantId ?? 0}`,
  );

  const selectedOptionContext = useSelectedOptionsContext();

  const { selectedOption, setSelectedOption } = selectedOptionContext;

  const prePurchaseModalContext = usePrePurchaseModalContext();

  // ### Stripe

  const [hasStripeError, setHasStripeError] = useState(false);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);

  const handleStripeLoadingError = useCallback((hasError: boolean) => {
    setHasStripeError(hasError);
  }, []);

  useEffect(() => {
    if (!stripePromise) {
      setStripePromise(loadStripe(stripePublicKey));
    }
  }, [stripePromise]);

  // determine Stripe button height based off of the height of the adjacent button
  const ref = React.useRef<HTMLButtonElement>(null);
  const [buttonHeight, setButtonHeight] = useState<number | null>(null);
  useEffect(() => {
    if (ref.current) {
      setButtonHeight(ref.current.clientHeight);
    }
  }, []);

  // ### Subscription
  const subscriptionVariants = variants?.filter(
    (variant) => variant?.subscription_plan,
  );

  const subscriptionOptions =
    subscriptionVariants
      ?.map((variant) => {
        if (!variant || !variant.subscription_plan) return null;
        return {
          id: variant?.id ?? 0,
          name: variant?.subscription_plan?.name ?? '',
          unit: variant?.subscription_plan?.interval ?? '',
          price: variant?.price ?? '0',
          percentDiscount: variant?.subscription_plan?.percent_discount ?? 0,
        };
      })
      .filter(Boolean) ?? [];
  const singleSubscriptionOption = subscriptionOptions[0];
  const hasSubscriptionOptions = !!singleSubscriptionOption;
  const isVitaminProduct = VITAMINS_AND_SUPPLEMENTS.includes(
    props.product?.slug ?? '',
  );

  // ### Selection state

  const [memberShipOptionSelected, setMemberShipOptionSelected] = useState<
    'monthly' | 'annually'
  >('monthly');

  // ### Pricing

  const oneTimePurchasePrice = Number(productVariant?.price ?? '0');
  const oneTimePurchaseDiscountedPrice = activePromo
    ? parseFloat(discountedPrice(oneTimePurchasePrice, activePromo))
    : undefined;

  const membershipPrice = memberShipOptionSelected === 'annually' ? 449 : 39;

  const subscriptionPrice = Number(singleSubscriptionOption?.price ?? '0');

  const showPromotion =
    Boolean(activePromo) &&
    selectedOption === 'oneTimePurchase' &&
    oneTimePurchaseDiscountedPrice !== undefined &&
    showPromo;

  const displayPrice = {
    oneTimePurchase: showPromotion
      ? oneTimePurchaseDiscountedPrice
      : oneTimePurchasePrice,
    membership: membershipPrice,
    subscription: subscriptionPrice,
  }[selectedOption];

  const displayPriceBeforeDiscount = {
    oneTimePurchase: showPromotion ? oneTimePurchasePrice : undefined,
    membership: undefined,
    subscription: undefined,
  }[selectedOption];

  // ### Cart interactions
  const { order, addItemToCartAsync, isAddItemToCartLoading } = useCart();
  const isLoading = !Boolean(order) || isAddItemToCartLoading;

  const trackWhenAddToCartClicked = (): void => {
    analytics.track({
      data: {
        label: 'PDP',
        'Test ID': productVariantId ?? 0,
        'Test Name': name ?? '',
        'Purchase Type': selectedOption,
        Sku: sku ? sku : '',
        Experiment: Cookies.get(CookieNames.HomepageExperiment) ?? '',
      },
      event: ANALYTICS.EVENTS.ADD_TO_CART,
    });
  };

  const variantId =
    selectedOption === 'subscription'
      ? Number(singleSubscriptionOption?.id ?? '0')
      : productVariantId;

  // Add a variant to the cart

  const handleAddToCart = async ({ variantId }: { variantId: number }) => {
    try {
      await addItemToCartAsync({
        variant_id: variantId,
        quantity: 1,
      });
    } catch (error) {
      logError('There was an issue adding the item to the cart', {
        component: 'PurchaseOptions',
        method: 'handleAddToCart',
        experience: 'PDP',
      });
    }
  };

  // add either a subscription or one-time purchase to the cart
  const handleAddToCartClick = async () => {
    if (isQuestProduct) {
      analytics.track({
        event: ANALYTICS.EVENTS.CLICKED_BUTTON,
        data: {
          label: ANALYTICS.LABELS.QUEST_BUY_TEST,
          component: 'PurchaseOptions',
          product: product.name ?? '',
        },
      });
      navigate(`${CHECKOUT_QUEST_BASE_PATH}/?variant=${productVariantId}`);
      return;
    }
    if (isProductOutOfStock) return;

    if (hasPrePurchaseStep) {
      prePurchaseModalContext?.dispatch({
        type: PrePurchaseModalActionsTypes.SetPrePurchaseScreener,
        payload: {
          isPrePurchaseScreenerOpen: true,
          prePurchaseScreenerData: screener,
          generalDisclaimerData: disclaimer,
        },
      });
    }

    if (!hasPrePurchaseStep && variantId) {
      await handleAddToCart({
        variantId,
      });

      trackWhenAddToCartClicked();
    } else {
      // log error
      logError('Variant ID is missing', {
        component: 'PurchaseOptions',
        method: 'handleAddToCartClick',
        experience: 'PDP',
      });
    }
  };

  // ### Express Pay
  const toast = useToastContext();
  // express pay total is in cents
  // note: express pay only works for one time purchase, but regardless if it changes in the future, we should charge what the user sees
  const expressPayTotal = displayPrice * 100;

  /* unable to test stripe in our unit tests at the moment */
  /* istanbul ignore next */
  const handleExpressPayPaymentConfirm: StripePaymentProps['handlePaymentConfirm'] =
    async (data, token) => {
      try {
        if (productVariantId) {
          const response = await processExpressPayOrder({
            type: 'singleItem',
            variantId: productVariantId,
            promoCode: showPromotion
              ? activePromo?.code ?? undefined
              : undefined,
            data,
            token,
          });
          navigate(
            `/confirmation?orderId=${response.data.id}&registration_token=${response.data.registration_token}&referral=true`,
          );
        }
      } catch (e: any) {
        const error = e as unknown as Error;
        let errorMessage =
          'Oops! Something went wrong. Please try again or contact support for help';
        if (!/(network|syntax)/i.test(error.message)) {
          errorMessage = error.message;
        }
        toast.showToast({
          message: errorMessage,
          status: 'error',
        });
        logError('Error processing express pay payment', {
          component: 'PurchaseOptions',
          method: 'handleExpressPayPaymentConfirm',
          experience: 'PDP',
        });
      }
    };

  const membershipLink = `/monthly-at-home-lab-testing-membership/?slug=${
    memberShipOptionSelected === 'annually'
      ? MembershipOptionType.YearlyMembership
      : MembershipOptionType.MonthlyMembership
  }`;

  return (
    <>
      <Flex flexDirection="column">
        <Grid as="section" aria-label="Payment total" marginBottom={6}>
          <GridItem>
            <Flex
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="baseline"
            >
              {showPromotion ? (
                <Flex gap={3} alignItems="baseline">
                  {displayPriceBeforeDiscount && (
                    <Price
                      price={displayPriceBeforeDiscount}
                      fontSize="5xl"
                      color="tints.uiGrey"
                      strikeThrough
                    />
                  )}

                  <Box color={'utility.sale'}>
                    <Price price={displayPrice} fontSize="5xl" />
                  </Box>

                  <Flex alignItems="baseline" gap={0.5}>
                    <Text fontSize="md" color="utility.sale">
                      with code
                    </Text>

                    <Text fontSize="xl" color="utility.sale">
                      {activePromo?.code?.toUpperCase()}
                    </Text>
                  </Flex>
                </Flex>
              ) : (
                <Price price={displayPrice} fontSize="5xl" />
              )}
            </Flex>
          </GridItem>
        </Grid>
        {!isProductOutOfStock &&
          !isEliminationDiet &&
          !isQuestProduct &&
          !hidePaymentOptions && (
            <Flex
              flexDirection="column"
              as="section"
              aria-label="Payment options"
            >
              {/* Only show subscription options for vitamins for the time being */}
              {hasSubscriptionOptions && isVitaminProduct ? (
                <HStack spacing={4}>
                  <SimplePurchaseOption
                    isSelected={selectedOption === 'subscription'}
                    onClick={() => setSelectedOption('subscription')}
                    label={`${singleSubscriptionOption.name} subscription`}
                    description={
                      <Text
                        size="sm"
                        color="utility.sale"
                      >{`Save ${singleSubscriptionOption.percentDiscount}%`}</Text>
                    }
                  />
                  <SimplePurchaseOption
                    isSelected={selectedOption === 'oneTimePurchase'}
                    onClick={() => setSelectedOption('oneTimePurchase')}
                    label="One-time purchase"
                    description="30-day supply"
                  />
                </HStack>
              ) : (
                <Box>
                  {/* Otherwise show one time purchase and membership options for the rest of the products */}
                  <SimplePurchaseOption
                    isSelected={selectedOption === 'oneTimePurchase'}
                    onClick={() => setSelectedOption('oneTimePurchase')}
                    price={{ value: oneTimePurchasePrice, unit: 'each' }}
                    label="One-time purchase"
                    description="Test kit & results"
                  />
                  <Box marginTop={4}>
                    <Flex justifyContent={'space-between'} marginBottom={2}>
                      <Text color="tints.darkGrey">Save with a membership</Text>
                      <Link
                        as={GatsbyLink}
                        to="/monthly-at-home-lab-testing-membership"
                        sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                      >
                        <span>Learn more</span>
                        <Icon as={ArrowSquareOut} />
                      </Link>
                    </Flex>

                    <MembershipOptions
                      isSelected={selectedOption === 'membership'}
                      selectedValue={memberShipOptionSelected}
                      onRadioGroupChange={(value) =>
                        setMemberShipOptionSelected(
                          value as 'monthly' | 'annually',
                        )
                      }
                      onClick={() => setSelectedOption('membership')}
                    />
                  </Box>
                </Box>
              )}
            </Flex>
          )}

        <Grid
          as="section"
          aria-label="Payment actions"
          display="flex"
          alignItems={'center'}
          marginTop={8}
          gridGap={3}
          align="stretch"
          justifyContent={'space-between'}
        >
          <GridItem>
            <Flex
              flexDirection={'row'}
              width={'100%'}
              justifyContent={'space-between'}
              alignItems={'stretch'}
              gap={2}
            >
              <Box flex={1}>
                {isProductOutOfStock ||
                selectedOption !== 'membership' ||
                isEliminationDiet ||
                isQuestProduct ? (
                  <Button
                    ref={ref}
                    onClick={() => handleAddToCartClick()}
                    isLoading={isLoading}
                    isDisabled={isProductOutOfStock}
                    data-testid="desktopAddToCartButton"
                    width={'100%'}
                  >
                    {isProductOutOfStock
                      ? 'Out of stock'
                      : isQuestProduct
                      ? 'Buy test'
                      : 'Add to cart'}
                  </Button>
                ) : (
                  <Button
                    ref={ref}
                    rightIcon={<Icon as={ArrowRight} />}
                    as={GatsbyLink}
                    to={membershipLink}
                    width={'100%'}
                  >
                    Join today
                  </Button>
                )}
              </Box>

              {!isProductOutOfStock &&
              (selectedOption === 'oneTimePurchase' || isEliminationDiet) &&
              !isQuestProduct &&
              !hasStripeError &&
              buttonHeight ? (
                <Box flex={1}>
                  <Elements stripe={stripePromise}>
                    <StripePayment
                      buttonHeight={buttonHeight}
                      cartTotal={expressPayTotal}
                      label={ANALYTICS.LABELS.EXPRESS_PAY_PDP}
                      variantsSkus={sku ? [sku] : []}
                      onStripeLoadingError={handleStripeLoadingError}
                      withApplePayWidget={false}
                      handlePaymentConfirm={handleExpressPayPaymentConfirm}
                    />
                  </Elements>
                </Box>
              ) : null}
            </Flex>
            {!isProductOutOfStock &&
            enableStripeUpgrade &&
            !isQuestProduct &&
            !isVitaminProduct ? (
              <Flex marginTop={4} justifyContent="center">
                <StripeMerchandisingWidget amount={expressPayTotal} />
              </Flex>
            ) : null}
          </GridItem>

          <GridItem>
            <IncentivesList contentfulFlags={contentfulFlags} />
          </GridItem>
        </Grid>
      </Flex>
    </>
  );
};

export default PurchaseOptions;
